import React from 'react';
import { Container } from '../components/ui/Container';
export function EULAPage() {
  return <div className="bg-white py-20 lg:py-28" data-id="element-436">
      <Container size="md" data-id="element-437">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-8" data-id="element-438">
          End User License Agreement
        </h1>
        <p className="text-gray-500 mb-12" data-id="element-439">Last updated: December 15, 2024</p>

        <div className="prose prose-lg prose-indigo max-w-none" data-id="element-440">
          <p className="text-xl text-gray-600 mb-8" data-id="element-441">
            This End User License Agreement ("EULA") is a legal agreement
            between you ("Licensee", "you", or "your") and Acciom Limited
            ("Licensor", "we", "us", or "our") for the use of the Flowsense
            software platform and related services ("Software").
          </p>

          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 mb-8" data-id="element-442">
            <p className="text-sm text-indigo-900 font-semibold mb-2" data-id="element-443">
              IMPORTANT NOTICE
            </p>
            <p className="text-sm text-indigo-800 mb-0" data-id="element-444">
              By installing, accessing, or using the Software, you acknowledge
              that you have read, understood, and agree to be bound by this
              EULA. If you do not agree to these terms, do not install, access,
              or use the Software.
            </p>
          </div>

          <h2 data-id="element-445">1. Definitions</h2>
          <ul data-id="element-446">
            <li data-id="element-447">
              <strong data-id="element-448">"Software"</strong> means the Flowsense SaaS platform,
              including all web applications, APIs, mobile applications (if
              applicable), documentation, and updates provided by Licensor.
            </li>
            <li data-id="element-449">
              <strong data-id="element-450">"Account"</strong> means your registered user account that
              provides access to the Software.
            </li>
            <li data-id="element-451">
              <strong data-id="element-452">"Client Data"</strong> means data you provide about your
              clients, including client names, website URLs, and notes.
            </li>
            <li data-id="element-453">
              <strong data-id="element-454">"Klaviyo Data"</strong> means data synced from your
              Klaviyo account via OAuth, including flow configurations,
              templates, and performance metrics.
            </li>
            <li data-id="element-455">
              <strong data-id="element-456">"Subscription"</strong> means your paid or free plan that
              determines your access level and feature availability.
            </li>
            <li data-id="element-457">
              <strong data-id="element-458">"Documentation"</strong> means user guides, help articles,
              API documentation, and other materials provided by Licensor.
            </li>
          </ul>

          <h2 data-id="element-459">2. License Grant</h2>

          <h3 data-id="element-460">2.1 Scope of License</h3>
          <p data-id="element-461">
            Subject to your compliance with this EULA and payment of applicable
            fees, Licensor grants you a limited, non-exclusive,
            non-transferable, non-sublicensable, revocable license to:
          </p>
          <ul data-id="element-462">
            <li data-id="element-463">
              Access and use the Software via a web browser or approved client
              applications
            </li>
            <li data-id="element-464">Use the Software for your internal business purposes only</li>
            <li data-id="element-465">
              Access and use Documentation to support your use of the Software
            </li>
            <li data-id="element-466">Connect your Klaviyo account(s) to the Software via OAuth</li>
            <li data-id="element-467">Generate and export reports and recommendations</li>
          </ul>

          <h3 data-id="element-468">2.2 License Limitations</h3>
          <p data-id="element-469">
            Your license is subject to the following limitations based on your
            Subscription plan:
          </p>
          <ul data-id="element-470">
            <li data-id="element-471">
              <strong data-id="element-472">Free Plan:</strong> 1 client, basic features, community
              support
            </li>
            <li data-id="element-473">
              <strong data-id="element-474">Pro Plan:</strong> Unlimited clients, full features,
              priority support
            </li>
            <li data-id="element-475">
              <strong data-id="element-476">Agency Plan:</strong> Unlimited clients, white-label
              features, team collaboration, dedicated support
            </li>
          </ul>
          <p data-id="element-477">
            Exceeding your plan limits may result in service restrictions or
            automatic upgrade to a higher-tier plan.
          </p>

          <h3 data-id="element-478">2.3 SaaS Model</h3>
          <p data-id="element-479">
            The Software is provided as a hosted Software-as-a-Service (SaaS)
            solution. You do not receive any software to download or install.
            All Software components remain on Licensor's servers or authorized
            cloud infrastructure (AWS).
          </p>

          <h2 data-id="element-480">3. License Restrictions</h2>

          <h3 data-id="element-481">3.1 Prohibited Activities</h3>
          <p data-id="element-482">You shall not, and shall not permit any third party to:</p>
          <ul data-id="element-483">
            <li data-id="element-484">
              <strong data-id="element-485">Reverse Engineering:</strong> Reverse engineer, decompile,
              disassemble, or attempt to derive the source code of the Software
            </li>
            <li data-id="element-486">
              <strong data-id="element-487">Modification:</strong> Modify, translate, adapt, or create
              derivative works based on the Software
            </li>
            <li data-id="element-488">
              <strong data-id="element-489">Redistribution:</strong> Rent, lease, lend, sell,
              sublicense, assign, distribute, publish, transfer, or otherwise
              make available the Software to any third party
            </li>
            <li data-id="element-490">
              <strong data-id="element-491">Circumvention:</strong> Circumvent or disable any security
              features, license keys, or access controls
            </li>
            <li data-id="element-492">
              <strong data-id="element-493">Competitive Use:</strong> Use the Software to develop,
              support, or operate a competing product or service
            </li>
            <li data-id="element-494">
              <strong data-id="element-495">Scraping:</strong> Use automated tools to scrape, crawl,
              or extract data from the Software
            </li>
            <li data-id="element-496">
              <strong data-id="element-497">Benchmarking:</strong> Conduct competitive analysis or
              benchmark testing without prior written consent
            </li>
            <li data-id="element-498">
              <strong data-id="element-499">Overloading:</strong> Overload or attempt to disrupt the
              Software's servers or networks
            </li>
            <li data-id="element-500">
              <strong data-id="element-501">Unauthorized Access:</strong> Access parts of the Software
              you are not authorized to access
            </li>
            <li data-id="element-502">
              <strong data-id="element-503">IP Removal:</strong> Remove, alter, or obscure any
              copyright, trademark, or other proprietary notices
            </li>
          </ul>

          <h3 data-id="element-504">3.2 API Usage Restrictions</h3>
          <p data-id="element-505">If you use the Software's API (when available), you agree to:</p>
          <ul data-id="element-506">
            <li data-id="element-507">Comply with rate limits and usage quotas</li>
            <li data-id="element-508">Not use the API to replicate core Software functionality</li>
            <li data-id="element-509">Not resell or redistribute API access</li>
            <li data-id="element-510">Use the API only for your internal business purposes</li>
          </ul>

          <h2 data-id="element-511">4. Intellectual Property Rights</h2>

          <h3 data-id="element-512">4.1 Licensor's Ownership</h3>
          <p data-id="element-513">
            The Software, including all software code, algorithms, AI models,
            user interfaces, designs, graphics, text, Documentation, and other
            content, is the exclusive property of Acciom Limited and its
            licensors. The Software is protected by:
          </p>
          <ul data-id="element-514">
            <li data-id="element-515">Copyright laws and international copyright treaties</li>
            <li data-id="element-516">Trademark laws</li>
            <li data-id="element-517">Patent laws (where applicable)</li>
            <li data-id="element-518">Trade secret laws</li>
            <li data-id="element-519">Other intellectual property laws and treaties</li>
          </ul>
          <p data-id="element-520">
            This EULA grants you a license to use the Software but does not
            transfer any ownership rights.
          </p>

          <h3 data-id="element-521">4.2 Licensee's Data Ownership</h3>
          <p data-id="element-522">You retain all ownership rights to:</p>
          <ul data-id="element-523">
            <li data-id="element-524">
              <strong data-id="element-525">Client Data:</strong> Information you enter about your
              clients
            </li>
            <li data-id="element-526">
              <strong data-id="element-527">Klaviyo Data:</strong> Data synced from your Klaviyo
              account (you own this data; we only process it)
            </li>
            <li data-id="element-528">
              <strong data-id="element-529">Custom Content:</strong> Notes, annotations, and custom
              reports you create
            </li>
          </ul>
          <p data-id="element-530">
            By using the Software, you grant Licensor a limited, non-exclusive,
            royalty-free license to:
          </p>
          <ul data-id="element-531">
            <li data-id="element-532">Store and process your data to provide the Software</li>
            <li data-id="element-533">
              Use anonymized, aggregated data to improve AI models and features
            </li>
            <li data-id="element-534">Display your data back to you in reports and dashboards</li>
          </ul>

          <h3 data-id="element-535">4.3 AI-Generated Content</h3>
          <p data-id="element-536">
            Recommendations and insights generated by the Software's AI models
            are provided to you for your use. However:
          </p>
          <ul data-id="element-537">
            <li data-id="element-538">
              Licensor retains ownership of the underlying AI models and
              algorithms
            </li>
            <li data-id="element-539">
              You may use AI-generated recommendations for your business
              purposes
            </li>
            <li data-id="element-540">You may not extract or reverse-engineer the AI models</li>
            <li data-id="element-541">
              Recommendations are provided "as is" without warranties of
              accuracy or effectiveness
            </li>
          </ul>

          <h3 data-id="element-542">4.4 Trademarks</h3>
          <p data-id="element-543">
            "Flowsense", the Flowsense logo, and other marks are trademarks of
            Acciom Limited. You may not use these trademarks without prior
            written permission, except as necessary to identify the Software in
            accordance with this EULA.
          </p>

          <h3 data-id="element-544">4.5 White-Label Usage (Agency Plan Only)</h3>
          <p data-id="element-545">
            If you subscribe to the Agency Plan with white-label features, you
            may:
          </p>
          <ul data-id="element-546">
            <li data-id="element-547">Use custom domains for client-facing reports</li>
            <li data-id="element-548">Remove Flowsense branding from exported reports</li>
            <li data-id="element-549">Add your own logo and branding to reports</li>
          </ul>
          <p data-id="element-550">You may not:</p>
          <ul data-id="element-551">
            <li data-id="element-552">Claim ownership of the Software</li>
            <li data-id="element-553">Represent the Software as your own product</li>
            <li data-id="element-554">
              Remove copyright notices from the Software itself (only from
              exported reports)
            </li>
          </ul>

          <h2 data-id="element-555">5. Data Privacy and Security</h2>

          <h3 data-id="element-556">5.1 Data Processing</h3>
          <p data-id="element-557">
            Licensor processes your data in accordance with our Privacy Policy,
            available at{' '}
            <a href="https://useflowsense.com/privacy" data-id="element-558">
              useflowsense.com/privacy
            </a>
            . By using the Software, you consent to such processing.
          </p>

          <h3 data-id="element-559">5.2 Data Security Measures</h3>
          <p data-id="element-560">
            Licensor implements industry-standard security measures to protect
            your data, including:
          </p>
          <ul data-id="element-561">
            <li data-id="element-562">Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
            <li data-id="element-563">Role-based access control (RBAC)</li>
            <li data-id="element-564">Regular security audits and penetration testing</li>
            <li data-id="element-565">Automated backup and disaster recovery procedures</li>
            <li data-id="element-566">24/7 security monitoring and incident response</li>
          </ul>

          <h3 data-id="element-567">5.3 Data Location</h3>
          <p data-id="element-568">
            Your data is stored on secure AWS servers located in the United
            Kingdom and/or European Union. Data may be transferred to the United
            States for processing by third-party service providers (e.g., OpenAI
            for AI recommendations) in accordance with applicable data
            protection laws.
          </p>

          <h3 data-id="element-569">5.4 Data Retention</h3>
          <p data-id="element-570">Licensor retains your data as follows:</p>
          <ul data-id="element-571">
            <li data-id="element-572">
              <strong data-id="element-573">Active Accounts:</strong> Data is retained for the
              duration of your Subscription
            </li>
            <li data-id="element-574">
              <strong data-id="element-575">Deleted Accounts:</strong> Data is permanently deleted
              within 30 days of account deletion
            </li>
            <li data-id="element-576">
              <strong data-id="element-577">Backups:</strong> Deleted data may persist in encrypted
              backups for up to 30 days
            </li>
            <li data-id="element-578">
              <strong data-id="element-579">Legal Retention:</strong> Some data may be retained longer
              if required by law (e.g., financial records)
            </li>
          </ul>

          <h3 data-id="element-580">5.5 Data Breach Notification</h3>
          <p data-id="element-581">
            In the event of a data breach affecting your Account, Licensor will
            notify you via email within 72 hours of discovering the breach and
            provide information about the nature of the breach and remediation
            steps.
          </p>

          <h2 data-id="element-582">6. Third-Party Services</h2>

          <h3 data-id="element-583">6.1 Klaviyo Integration</h3>
          <p data-id="element-584">
            The Software integrates with Klaviyo via OAuth 2.0. By connecting
            your Klaviyo account, you:
          </p>
          <ul data-id="element-585">
            <li data-id="element-586">
              Authorize Flowsense to access specific Klaviyo data (flows,
              templates, metrics)
            </li>
            <li data-id="element-587">
              Acknowledge that Klaviyo's Terms of Service and Privacy Policy
              apply to your Klaviyo account
            </li>
            <li data-id="element-588">
              Understand that Licensor is not responsible for Klaviyo's service
              availability or data practices
            </li>
            <li data-id="element-589">
              Agree that Licensor may access your Klaviyo data only as
              authorized via OAuth
            </li>
          </ul>

          <h3 data-id="element-590">6.2 Payment Processing (Stripe)</h3>
          <p data-id="element-591">
            Payments are processed by Stripe, Inc. By providing payment
            information, you agree to Stripe's Terms of Service and Privacy
            Policy. Licensor does not store your full credit card numbers.
          </p>

          <h3 data-id="element-592">6.3 AI Services (OpenAI)</h3>
          <p data-id="element-593">
            The Software uses OpenAI's API to generate recommendations. When
            using AI features:
          </p>
          <ul data-id="element-594">
            <li data-id="element-595">Anonymized flow data is sent to OpenAI for processing</li>
            <li data-id="element-596">No customer PII from your Klaviyo account is sent to OpenAI</li>
            <li data-id="element-597">OpenAI's Terms of Use and Privacy Policy apply to API usage</li>
            <li data-id="element-598">
              Licensor is not responsible for OpenAI's service availability or
              data practices
            </li>
          </ul>

          <h3 data-id="element-599">6.4 Other Third-Party Services</h3>
          <p data-id="element-600">
            The Software may integrate with other third-party services
            (analytics, monitoring, etc.). Your use of these services is subject
            to their respective terms and privacy policies.
          </p>

          <h2 data-id="element-601">7. Updates and Maintenance</h2>

          <h3 data-id="element-602">7.1 Software Updates</h3>
          <p data-id="element-603">Licensor may, in its sole discretion:</p>
          <ul data-id="element-604">
            <li data-id="element-605">Provide updates, upgrades, patches, and new features</li>
            <li data-id="element-606">Modify or discontinue features</li>
            <li data-id="element-607">Change the Software's functionality or user interface</li>
          </ul>
          <p data-id="element-608">
            Updates are provided automatically as part of the SaaS model. You do
            not need to take any action to receive updates.
          </p>

          <h3 data-id="element-609">7.2 No Obligation to Update</h3>
          <p data-id="element-610">
            Licensor has no obligation to provide updates or continue supporting
            any particular feature. Features may be added, modified, or removed
            at any time.
          </p>

          <h3 data-id="element-611">7.3 Scheduled Maintenance</h3>
          <p data-id="element-612">
            Licensor may perform scheduled maintenance that temporarily
            interrupts service. We will provide advance notice of scheduled
            maintenance when possible, typically via:
          </p>
          <ul data-id="element-613">
            <li data-id="element-614">Email notification</li>
            <li data-id="element-615">In-app notification</li>
            <li data-id="element-616">Status page updates</li>
          </ul>

          <h3 data-id="element-617">7.4 Emergency Maintenance</h3>
          <p data-id="element-618">
            Licensor may perform emergency maintenance without advance notice if
            necessary to address security vulnerabilities, critical bugs, or
            service disruptions.
          </p>

          <h2 data-id="element-619">8. Support and Services</h2>

          <h3 data-id="element-620">8.1 Support Levels</h3>
          <p data-id="element-621">Support is provided based on your Subscription plan:</p>
          <ul data-id="element-622">
            <li data-id="element-623">
              <strong data-id="element-624">Free Plan:</strong> Community support via documentation
              and help articles
            </li>
            <li data-id="element-625">
              <strong data-id="element-626">Pro Plan:</strong> Email support with 24-hour response
              time (business days)
            </li>
            <li data-id="element-627">
              <strong data-id="element-628">Agency Plan:</strong> Priority email support with 4-hour
              response time, dedicated account manager (on request)
            </li>
          </ul>

          <h3 data-id="element-629">8.2 Support Scope</h3>
          <p data-id="element-630">Support includes:</p>
          <ul data-id="element-631">
            <li data-id="element-632">Assistance with Software functionality and features</li>
            <li data-id="element-633">Troubleshooting technical issues</li>
            <li data-id="element-634">Guidance on best practices</li>
            <li data-id="element-635">Account and billing inquiries</li>
          </ul>
          <p data-id="element-636">Support does not include:</p>
          <ul data-id="element-637">
            <li data-id="element-638">Custom development or feature requests</li>
            <li data-id="element-639">
              Klaviyo account setup or troubleshooting (contact Klaviyo support)
            </li>
            <li data-id="element-640">Marketing strategy consulting</li>
            <li data-id="element-641">
              Implementation of recommendations (you are responsible for
              implementation)
            </li>
          </ul>

          <h3 data-id="element-642">8.3 Support Hours</h3>
          <p data-id="element-643">
            Support is available Monday through Friday, 9:00 AM to 5:00 PM GMT,
            excluding UK public holidays. Emergency support for critical issues
            may be available outside business hours for Agency Plan subscribers.
          </p>

          <h2 data-id="element-644">9. Warranties and Disclaimers</h2>

          <h3 data-id="element-645">9.1 Limited Warranty</h3>
          <p data-id="element-646">
            Licensor warrants that the Software will perform substantially in
            accordance with the Documentation under normal use. This warranty is
            valid for 14 days from the date of your initial Subscription.
          </p>

          <h3 data-id="element-647">9.2 Warranty Disclaimer</h3>
          <p data-id="element-648">
            EXCEPT FOR THE LIMITED WARRANTY ABOVE, THE SOFTWARE IS PROVIDED "AS
            IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul data-id="element-649">
            <li data-id="element-650">
              Warranties of merchantability, fitness for a particular purpose,
              or non-infringement
            </li>
            <li data-id="element-651">
              Warranties that the Software will be uninterrupted, error-free, or
              secure
            </li>
            <li data-id="element-652">
              Warranties regarding the accuracy, reliability, or completeness of
              AI-generated recommendations
            </li>
            <li data-id="element-653">Warranties that defects will be corrected</li>
            <li data-id="element-654">
              Warranties regarding third-party service availability (Klaviyo,
              OpenAI, etc.)
            </li>
          </ul>

          <h3 data-id="element-655">9.3 AI Recommendations Disclaimer</h3>
          <p data-id="element-656">
            AI-generated recommendations are based on algorithms and data
            analysis. Licensor does not warrant:
          </p>
          <ul data-id="element-657">
            <li data-id="element-658">The accuracy or effectiveness of recommendations</li>
            <li data-id="element-659">That implementing recommendations will improve your results</li>
            <li data-id="element-660">
              That recommendations are suitable for your specific business needs
            </li>
            <li data-id="element-661">
              That recommendations comply with all applicable laws and
              regulations
            </li>
          </ul>
          <p data-id="element-662">
            You are solely responsible for evaluating, testing, and implementing
            recommendations. Licensor recommends A/B testing changes before full
            implementation.
          </p>

          <h3 data-id="element-663">9.4 No Professional Advice</h3>
          <p data-id="element-664">
            The Software does not provide legal, financial, marketing, or other
            professional advice. You should consult with qualified professionals
            before making business decisions based on Software recommendations.
          </p>

          <h2 data-id="element-665">10. Limitation of Liability</h2>

          <h3 data-id="element-666">10.1 Exclusion of Damages</h3>
          <p data-id="element-667">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL LICENSOR,
            ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS
            BE LIABLE FOR:
          </p>
          <ul data-id="element-668">
            <li data-id="element-669">
              Any indirect, incidental, special, consequential, or punitive
              damages
            </li>
            <li data-id="element-670">
              Loss of profits, revenue, data, use, goodwill, or other intangible
              losses
            </li>
            <li data-id="element-671">
              Damages resulting from unauthorized access to or alteration of
              your data
            </li>
            <li data-id="element-672">
              Damages resulting from Software errors, bugs, or interruptions
            </li>
            <li data-id="element-673">
              Damages resulting from third-party services (Klaviyo, OpenAI,
              etc.)
            </li>
            <li data-id="element-674">Damages resulting from implementation of AI recommendations</li>
            <li data-id="element-675">Damages resulting from your violation of this EULA</li>
          </ul>
          <p data-id="element-676">
            This limitation applies regardless of the legal theory (contract,
            tort, negligence, strict liability, or otherwise) and even if
            Licensor has been advised of the possibility of such damages.
          </p>

          <h3 data-id="element-677">10.2 Maximum Liability Cap</h3>
          <p data-id="element-678">
            Licensor's total aggregate liability to you for all claims arising
            from or related to the Software shall not exceed the greater of:
          </p>
          <ul data-id="element-679">
            <li data-id="element-680">
              The total amount you paid to Licensor in the 12 months preceding
              the claim, or
            </li>
            <li data-id="element-681">£100 (one hundred pounds sterling)</li>
          </ul>

          <h3 data-id="element-682">10.3 Essential Purpose</h3>
          <p data-id="element-683">
            The limitations in this section apply even if any remedy fails of
            its essential purpose.
          </p>

          <h3 data-id="element-684">10.4 Jurisdictional Limitations</h3>
          <p data-id="element-685">
            Some jurisdictions do not allow the exclusion or limitation of
            certain warranties or damages. In such jurisdictions, Licensor's
            liability will be limited to the maximum extent permitted by law.
          </p>

          <h2 data-id="element-686">11. Indemnification</h2>

          <h3 data-id="element-687">11.1 Your Indemnification Obligations</h3>
          <p data-id="element-688">
            You agree to indemnify, defend, and hold harmless Licensor, its
            officers, directors, employees, agents, affiliates, and licensors
            from and against any claims, liabilities, damages, losses, costs, or
            expenses (including reasonable attorneys' fees) arising from or
            related to:
          </p>
          <ul data-id="element-689">
            <li data-id="element-690">Your use or misuse of the Software</li>
            <li data-id="element-691">Your violation of this EULA or applicable laws</li>
            <li data-id="element-692">
              Your violation of any third-party rights (including intellectual
              property rights)
            </li>
            <li data-id="element-693">Your Client Data or Klaviyo Data</li>
            <li data-id="element-694">Your implementation of AI recommendations</li>
            <li data-id="element-695">
              Your breach of any representations or warranties in this EULA
            </li>
          </ul>

          <h3 data-id="element-696">11.2 Indemnification Process</h3>
          <p data-id="element-697">Licensor will:</p>
          <ul data-id="element-698">
            <li data-id="element-699">Promptly notify you of any claim subject to indemnification</li>
            <li data-id="element-700">Provide reasonable cooperation in the defense of the claim</li>
            <li data-id="element-701">
              Allow you to control the defense and settlement (subject to
              Licensor's approval of any settlement)
            </li>
          </ul>

          <h2 data-id="element-702">12. Term and Termination</h2>

          <h3 data-id="element-703">12.1 Term</h3>
          <p data-id="element-704">
            This EULA begins when you first access the Software and continues
            until terminated in accordance with this section.
          </p>

          <h3 data-id="element-705">12.2 Termination by You</h3>
          <p data-id="element-706">You may terminate this EULA at any time by:</p>
          <ul data-id="element-707">
            <li data-id="element-708">Canceling your Subscription and deleting your Account</li>
            <li data-id="element-709">Ceasing all use of the Software</li>
          </ul>
          <p data-id="element-710">
            Termination does not entitle you to a refund of any prepaid fees.
          </p>

          <h3 data-id="element-711">12.3 Termination by Licensor</h3>
          <p data-id="element-712">
            Licensor may terminate this EULA immediately, without prior notice,
            if:
          </p>
          <ul data-id="element-713">
            <li data-id="element-714">You violate any provision of this EULA</li>
            <li data-id="element-715">Your payment method fails repeatedly</li>
            <li data-id="element-716">You engage in fraudulent or illegal activity</li>
            <li data-id="element-717">Licensor is required to terminate by law or court order</li>
            <li data-id="element-718">Licensor discontinues the Software (with 30 days' notice)</li>
            <li data-id="element-719">
              Your Account poses a security risk to the Software or other users
            </li>
          </ul>

          <h3 data-id="element-720">12.4 Effect of Termination</h3>
          <p data-id="element-721">Upon termination:</p>
          <ul data-id="element-722">
            <li data-id="element-723">Your license to use the Software immediately ceases</li>
            <li data-id="element-724">You must cease all use of the Software</li>
            <li data-id="element-725">
              You remain liable for all fees incurred prior to termination
            </li>
            <li data-id="element-726">
              Licensor will delete your data in accordance with the Privacy
              Policy (within 30 days)
            </li>
            <li data-id="element-727">
              You may export your data before termination (if Account is
              accessible)
            </li>
          </ul>

          <h3 data-id="element-728">12.5 Survival</h3>
          <p data-id="element-729">
            The following sections survive termination: Intellectual Property
            Rights, Data Privacy and Security, Warranties and Disclaimers,
            Limitation of Liability, Indemnification, and General Provisions.
          </p>

          <h2 data-id="element-730">13. Export Compliance</h2>
          <p data-id="element-731">
            The Software may be subject to export control laws and regulations
            of the United Kingdom, European Union, United States, and other
            jurisdictions. You agree to:
          </p>
          <ul data-id="element-732">
            <li data-id="element-733">Comply with all applicable export and import laws</li>
            <li data-id="element-734">
              Not export or re-export the Software to prohibited countries or
              entities
            </li>
            <li data-id="element-735">
              Not use the Software for prohibited purposes (e.g., development of
              weapons)
            </li>
          </ul>
          <p data-id="element-736">You represent that you are not:</p>
          <ul data-id="element-737">
            <li data-id="element-738">
              Located in a country subject to a UK, EU, or U.S. government
              embargo
            </li>
            <li data-id="element-739">Designated as a "terrorist supporting" country</li>
            <li data-id="element-740">
              Listed on any government list of prohibited or restricted parties
            </li>
          </ul>

          <h2 data-id="element-741">14. Government Use</h2>
          <p data-id="element-742">
            If you are a government entity or using the Software on behalf of a
            government, the Software is "Commercial Computer Software" as
            defined in applicable regulations. Government use is subject to this
            EULA and applicable government procurement regulations.
          </p>

          <h2 data-id="element-743">15. General Provisions</h2>

          <h3 data-id="element-744">15.1 Entire Agreement</h3>
          <p data-id="element-745">
            This EULA, together with the Terms of Service and Privacy Policy,
            constitutes the entire agreement between you and Licensor regarding
            the Software and supersedes all prior agreements, understandings,
            and communications.
          </p>

          <h3 data-id="element-746">15.2 Amendments</h3>
          <p data-id="element-747">
            Licensor may amend this EULA at any time by posting the updated EULA
            on the website and notifying you via email. Continued use of the
            Software after changes take effect constitutes acceptance of the
            amended EULA.
          </p>

          <h3 data-id="element-748">15.3 Severability</h3>
          <p data-id="element-749">
            If any provision of this EULA is found to be invalid or
            unenforceable, the remaining provisions will remain in full force
            and effect, and the invalid provision will be modified to the
            minimum extent necessary to make it valid and enforceable.
          </p>

          <h3 data-id="element-750">15.4 Waiver</h3>
          <p data-id="element-751">
            Licensor's failure to enforce any right or provision of this EULA
            will not be deemed a waiver of such right or provision. Any waiver
            must be in writing and signed by Licensor.
          </p>

          <h3 data-id="element-752">15.5 Assignment</h3>
          <p data-id="element-753">
            You may not assign or transfer this EULA or your license without
            Licensor's prior written consent. Licensor may assign this EULA
            without restriction, including in connection with a merger,
            acquisition, or sale of assets.
          </p>

          <h3 data-id="element-754">15.6 Force Majeure</h3>
          <p data-id="element-755">
            Licensor will not be liable for any failure or delay in performance
            due to circumstances beyond its reasonable control, including acts
            of God, war, terrorism, riots, embargoes, acts of civil or military
            authorities, fire, floods, accidents, pandemics, strikes, or
            shortages of transportation, facilities, fuel, energy, labor, or
            materials.
          </p>

          <h3 data-id="element-756">15.7 Notices</h3>
          <p data-id="element-757">All notices under this EULA must be in writing and sent to:</p>
          <ul data-id="element-758">
            <li data-id="element-759">
              <strong data-id="element-760">To Licensor:</strong> support@useflowsense.com
            </li>
            <li data-id="element-761">
              <strong data-id="element-762">To You:</strong> The email address associated with your
              Account
            </li>
          </ul>
          <p data-id="element-763">
            Notices are deemed received when delivered via email (if no delivery
            failure notification is received).
          </p>

          <h3 data-id="element-764">15.8 Governing Law</h3>
          <p data-id="element-765">
            This EULA is governed by and construed in accordance with the laws
            of England and Wales, without regard to conflict of law principles.
          </p>

          <h3 data-id="element-766">15.9 Dispute Resolution</h3>
          <p data-id="element-767">
            Any disputes arising from or related to this EULA shall be subject
            to the exclusive jurisdiction of the courts of England and Wales.
            For users outside the EU and UK, disputes may be resolved through
            binding arbitration in accordance with the rules of the London Court
            of International Arbitration (LCIA).
          </p>

          <h3 data-id="element-768">15.10 Language</h3>
          <p data-id="element-769">
            This EULA is written in English. Any translations are provided for
            convenience only. In the event of a conflict, the English version
            shall prevail.
          </p>

          <h3 data-id="element-770">15.11 Headings</h3>
          <p data-id="element-771">
            Section headings are for convenience only and do not affect the
            interpretation of this EULA.
          </p>

          <h2 data-id="element-772">16. Contact Information</h2>
          <p data-id="element-773">
            If you have questions about this EULA or the Software, please
            contact us:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg not-prose" data-id="element-774">
            <p className="font-semibold text-gray-900 mb-2" data-id="element-775">
              Flowsense by Acciom Limited
            </p>
            <p className="text-gray-700 text-sm" data-id="element-776">
              <strong data-id="element-777">General Inquiries:</strong>{' '}
              <a href="mailto:support@useflowsense.com" className="text-indigo-600 hover:underline" data-id="element-778">
                support@useflowsense.com
              </a>
            </p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 mt-8" data-id="element-779">
            <p className="text-sm text-green-900 mb-2" data-id="element-780">
              <strong data-id="element-781">Acknowledgment</strong>
            </p>
            <p className="text-sm text-green-800 mb-0" data-id="element-782">
              By using the Software, you acknowledge that you have read this
              EULA, understand it, and agree to be bound by its terms and
              conditions. If you do not agree, do not use the Software.
            </p>
          </div>
        </div>
      </Container>
    </div>;
}